import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, Pencil, X } from 'lucide-react';
import { Abas } from '../../componentes/abas';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { anunciarOcultos } from '../../componentes/sensivel';
import { useToast } from '../../componentes/toast';
import { getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { data, dinheiro, mesAno, nomeParceiro, numero } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { DrawerContrato } from './Contratos';
import { invalidarAssinaturas } from './dados';
import { CAMINHO_REAJUSTES, type Reajuste } from './tipos';

type Filtro = 'pendente' | 'todos';

/** Cartão de um reajuste: contrato, índice, aniversário e acumulado, valor antigo riscado e o novo, e as ações. */
function CartaoReajuste({ r, destaque, aoEditar }: { r: Reajuste; destaque: boolean; aoEditar: (contratoId: string) => void }) {
  const { t } = useTranslation('assinaturas');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (destaque) ref.current?.scrollIntoView({ block: 'center' });
  }, [destaque]);
  const decidir = useMutation({
    mutationFn: async (acao: 'aprovar' | 'recusar') => ({ acao, r: await post<{ cobrancas_ajustadas?: number }>(`${CAMINHO_REAJUSTES}/${r.id}/${acao}`) }),
    onSuccess: async ({ acao, r: res }) => {
      await invalidarAssinaturas(qc);
      if (acao === 'aprovar') toast.sucesso(`${t('reajustes.aprovadoMsg')}. ${t('reajustes.ajustadas', { count: res?.cobrancas_ajustadas ?? 0 })}`);
      else toast.sucesso(t('reajustes.recusadoMsg'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const pendente = r.estado === 'pendente';
  const cor = r.estado === 'aprovado' ? 'verde' : r.estado === 'recusado' ? 'neutro' : 'laranja';
  return (
    // Review r16 visual B3: o `Cartao` do núcleo (antes um `article` próprio). O destaque vindo do aviso é um anel de 2px em
    // acento-forte, sem mexer na borda do cartão; editar o contrato é o botão de ícone do cabeçalho, e Aprovar e Recusar
    // cabem na mesma linha.
    <div ref={ref} data-reajuste={r.id} className={`min-w-0 rounded-xl ${destaque ? 'ring-2 ring-acento-forte' : ''}`}>
      <Cartao
        className="h-full"
        titulo={<span className="font-mono">{r.contrato_numero}</span>}
        acao={
          s.pode('assinaturas.editar') && (
            <Botao variante="fantasma" tamanho="sm" icone={<Pencil className="size-4" aria-hidden />} aria-label={t('acoes.editarContrato')} title={t('acoes.editarContrato')} onClick={() => aoEditar(r.contrato_id)} />
          )
        }
      >
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <p className="min-w-0 truncate text-sm text-tinta-2" title={nomeParceiro(r.parceiro_nome)}>
              {nomeParceiro(r.parceiro_nome)}
            </p>
            <div className="flex flex-wrap gap-1">
              <Selo cor="azul">{t(`indices.${r.indice}`)}</Selo>
              <Selo cor={cor}>{t(`reajustes.estado.${r.estado}`)}</Selo>
            </div>
          </div>
          <p className="text-sm text-tinta-2">
            <span>{t('reajustes.aniversario', { mes: mesAno(r.aniversario.slice(0, 7)) })}</span>
            <span aria-hidden> · </span>
            <span className="numero">{t('reajustes.acumulado', { pct: `+${numero(r.acumulado_pct, 2)}%` })}</span>
          </p>
          <p className="text-xs text-tinta-3">{t('reajustes.periodo', { de: mesAno(r.periodo_de.slice(0, 7)), ate: mesAno(r.periodo_ate.slice(0, 7)) })}</p>
          <dl className="flex flex-wrap items-end gap-x-6 gap-y-2">
            <div>
              <dt className="text-xs text-tinta-3">{t('reajustes.valorAntigo')}</dt>
              <dd className="numero text-tinta-3 line-through">{anunciarOcultos(dinheiro(r.valor_antigo_centavos, r.moeda))}</dd>
            </div>
            <div>
              <dt className="text-xs text-tinta-3">{t('reajustes.valorNovo')}</dt>
              <dd className="numero text-lg font-semibold text-tinta-1">{anunciarOcultos(dinheiro(r.valor_novo_centavos, r.moeda))}</dd>
            </div>
          </dl>
          {pendente ? <p className="text-xs text-tinta-3">{t('reajustes.semResposta')}</p> : r.decidido_em && <p className="numero text-xs text-tinta-3">{data(r.decidido_em.slice(0, 10))}</p>}
          {pendente && s.pode('assinaturas.reajustar') && (
            <div className="flex flex-wrap gap-2">
              {/* Review r16 visual M2: um "Aprovar" por cartão; secundário, para a aba nunca ter N primários. */}
              <Botao tamanho="sm" icone={<Check className="size-4" />} carregando={decidir.isPending && decidir.variables === 'aprovar'} onClick={() => decidir.mutate('aprovar')}>
                {t('acoes.aprovar')}
              </Botao>
              <Botao tamanho="sm" icone={<X className="size-4" />} carregando={decidir.isPending && decidir.variables === 'recusar'} onClick={() => decidir.mutate('recusar')}>
                {t('acoes.recusar')}
              </Botao>
            </div>
          )}
        </div>
      </Cartao>
    </div>
  );
}

/** Aba Reajustes (W12.9 e W12.11): filtro Pendentes e Todos e os cartões; o aviso da notificação destaca o cartão. */
export function Reajustes() {
  const { t } = useTranslation('assinaturas');
  const [busca] = useSearchParams();
  const destaque = busca.get('destaque');
  const [filtro, setFiltro] = useState<Filtro>('pendente');
  const [contrato, setContrato] = useState<string | null>(null);
  const lista = useQuery({
    queryKey: [CAMINHO_REAJUSTES, filtro],
    queryFn: () => getTodos<Reajuste>(CAMINHO_REAJUSTES, filtro === 'pendente' ? { estado: 'pendente' } : {}).then((r) => r.itens),
  });
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Review r16 visual B3: o filtro é o `Abas` do núcleo em chips (como o de papel em Pessoas e empresas), não uma terceira
          fileira de abas feita à mão. */}
      <Abas<Filtro>
        grupo="assinaturas-reajustes"
        variante="chips"
        rotulo={t('reajustes.filtro')}
        ativa={filtro}
        aoMudar={setFiltro}
        itens={(['pendente', 'todos'] as const).map((f) => ({ chave: f, rotulo: t(f === 'pendente' ? 'reajustes.pendentes' : 'reajustes.todos') }))}
      />
      {lista.isLoading ? (
        <Carregando />
      ) : lista.isError ? (
        <Erro erro={lista.error} aoTentar={() => void lista.refetch()} />
      ) : !lista.data?.length ? (
        <Vazio texto={t(filtro === 'pendente' ? 'vazio.reajustesPendentes' : 'vazio.reajustesTodos')} />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {lista.data.map((r) => (
            <CartaoReajuste key={r.id} r={r} destaque={r.id === destaque} aoEditar={setContrato} />
          ))}
        </div>
      )}
      {contrato && <DrawerContrato key={contrato} alvo={contrato} aoFechar={() => setContrato(null)} />}
    </div>
  );
}
